import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { createApiInstance, revokeTrustedDevice, getUserProfile } from '../api';
import { cachedFetch } from '../utils/fetchCache';
import socketService from '../services/socketService';
import { clearCredential } from '../services/biometricAuth';
import { getApiUrl, getMessengerUrl, getBackendUrl } from '../config/apiConfig';
// eslint-disable-next-line no-unused-vars
import { getImageUrl, getProfilePicUrl } from '../utils/urlHelper';
import {
  clearAllTrustedDeviceTokens,
  getOrCreateTrustedDeviceId,
  getTrustedDeviceAppId,
} from '../utils/trustedDevice';
import InfoTicker from './InfoTicker';
import EventCountdown from './EventCountdown';
import { getShortName } from '../utils/userDisplay';
import logger from '../utils/logger';
import { loadWhitelabelConfig } from '../utils/whitelabelConfig';
import useActivityLogger from '../hooks/useActivityLogger';
import { useContribution } from '../contexts/ContributionContext';
import { isNativePlatform } from '../services/biometricAuth';
import './TopBar.css';

const NEAR_ME_NEW_BADGE_EXPIRES_AT = '2026-08-31T23:59:59Z';

const TopBar = ({ onSidebarToggle, isOpen, isPinned }) => {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [violations, setViolations] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [adminPendingCounts, setAdminPendingCounts] = useState(null); // Unified admin pending counts
  const [showAdminActions, setShowAdminActions] = useState(false); // Admin Action Center dropdown
  const adminActionsRef = useRef(null); // Ref for click-outside detection
  const [showUserMenu, setShowUserMenu] = useState(false); // User avatar dropdown
  const userMenuRef = useRef(null);
  const { openPopup } = useContribution();
  const [unreadResponseCount, setUnreadResponseCount] = useState(0); // User unread admin responses
  const [urgencyCounts, setUrgencyCounts] = useState({ pending: 0, medium: 0, high: 0, critical: 0 }); // All urgency levels
  const [unattendedConversations, setUnattendedConversations] = useState([]); // For alert panel
  const [showUnattendedAlert, setShowUnattendedAlert] = useState(false); // Alert panel visibility
  const [brandConfig, setBrandConfig] = useState(null); // Whitelabel branding config

  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const searchMenuRef = useRef(null);
  const [savedSearchesForMenu, setSavedSearchesForMenu] = useState([]);
  const [savedSearchesForMenuLoading, setSavedSearchesForMenuLoading] = useState(false);
  const [savedSearchesForMenuLoadedAt, setSavedSearchesForMenuLoadedAt] = useState(null);
  const [nearMeRadiusMiles, setNearMeRadiusMiles] = useState(50);
  const [membershipStatus, setMembershipStatus] = useState(null);

  const getMembershipMicroLabel = useCallback((membership) => {
    if (!membership) return 'Loading...';
    if (!membership.hasAccess) return 'No membership';

    if (membership.type === 'one_time') {
      const largestPayment = Number(membership.largestPayment || 0);
      if (largestPayment >= 200) return '🙏 36-Months';
      if (largestPayment >= 175) return '🙏 24-Months';
      if (largestPayment >= 150) return '🙏 18-Months';
      if (largestPayment >= 100) return '🙏 12-Months';
      if (largestPayment >= 60) {
        const proratedMonths = Math.floor(largestPayment / 10);
        if (proratedMonths >= 6) return `🙏 ${proratedMonths}-Months`;
        return '✅ Active';
      }
      // For amounts < $60, show days at $1/day
      const days = Math.floor(largestPayment);
      if (days > 0) return `🙏 ${days} days`;
      return '✅ Active';
    }
    if (membership.type === '3_month') return '⏰ 3-Month';
    if (membership.type === '1_year') return '⭐ 1-Year';
    return '✅ Active';
  }, []);

  // Force refresh membership status
  const refreshMembershipStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');
      const membershipData = await cachedFetch(
        `contributionStatus:${username}`,
        async () => {
          const res = await fetch(`${getBackendUrl()}/api/contributions/contribution-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return res.ok ? res.json() : null;
        },
        60000,
        { force: true }
      );
      if (membershipData) {
        logger.debug('Membership status refreshed:', membershipData);
        setMembershipStatus(membershipData.membership);
      }
    } catch (error) {
      logger.error('Error refreshing membership status:', error);
    }
  }, []);
  const showNearMeNewBadge = Date.now() < new Date(NEAR_ME_NEW_BADGE_EXPIRES_AT).getTime();
  const [showMessengerMenu, setShowMessengerMenu] = useState(false);
  const messengerMenuRef = useRef(null);
  const [onlineCount, setOnlineCount] = useState(0);

  const clampNearMeRadius = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 50;
    return Math.max(10, Math.min(500, Math.round(parsed)));
  };

  // Compute highest urgency level for icon color
  const urgencyLevel = urgencyCounts.critical > 0 ? 'critical'
    : urgencyCounts.high > 0 ? 'high'
    : urgencyCounts.medium > 0 ? 'medium'
    : urgencyCounts.pending > 0 ? 'pending'
    : null;
  const navigate = useNavigate();
  const { logActivity, ActivityType } = useActivityLogger();

  // Load branding config
  useEffect(() => {
    loadWhitelabelConfig().then(cfg => setBrandConfig(cfg));
  }, []);

  // Subscribe to realtime online count updates (reuses existing socket pattern)
  useEffect(() => {
    if (!isLoggedIn) return;
    const handleOnlineCountUpdate = (data) => {
      setOnlineCount(data.count || 0);
    };
    socketService.on('online_count_update', handleOnlineCountUpdate);
    // Seed with current value
    try {
      const initial = socketService.getOnlineCount?.();
      if (typeof initial === 'number') setOnlineCount(initial);
    } catch (_e) { /* noop */ }
    return () => {
      socketService.off('online_count_update', handleOnlineCountUpdate);
    };
  }, [isLoggedIn]);

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    const params = new URLSearchParams(location.search);
    const adminSection = path === '/admin-hub' ? params.get('section') : null;
    if (path === '/dashboardv2') return { icon: '🦋', title: 'My Dashboard', subtitle: 'Overview & Activity' };
    if (path === '/dashboard') return { icon: '💑', title: 'My Dashboard', subtitle: 'View your matches and activity' };
    if (path === '/search') return { icon: '🔍', title: 'Search Profiles', subtitle: 'Find your perfect match' };
    if (path === '/messages') return { icon: '💬', title: 'My Messages', subtitle: 'Communicate with your connections' };
    if (path === '/favorites') return { icon: '⭐', title: 'Favorites', subtitle: 'Profiles you\'ve marked as favorites' };
    if (path === '/shortlist') return { icon: '📋', title: 'Shortlist', subtitle: 'Your curated list of potential matches' };
    if (path === '/exclusions') return { icon: '🚫', title: 'Exclusions', subtitle: 'Profiles you\'ve chosen to hide' };
    if (path === '/l3v3l-matches') return { icon: '🎯', title: 'L3V3L Matches', subtitle: 'AI-powered compatibility matches' };
    if (path === '/pii-management') return { icon: '🔒', title: 'Contacts & Photo Requests', subtitle: 'Manage who can access your contact info and photos' };
    if (path === '/requests') return { icon: '📬', title: 'Contact Requests', subtitle: 'Manage incoming contact requests' };
    if (path === '/preferences') return { icon: '🔔', title: 'Notification Preferences', subtitle: 'Customize your notification settings' };
    if (path === '/user-management') return { icon: '👥', title: 'User Management', subtitle: 'Manage user roles, permissions, and account status' };
    if (path === '/role-management') return { icon: '🎭', title: 'Role Management', subtitle: 'Configure user roles and permissions' };
    if (path.startsWith('/profile/')) return { icon: '👤', title: 'Profile', subtitle: 'View profile details' };
    if (path === '/settings') return { icon: '⚙️', title: 'Settings', subtitle: 'Manage your account settings' };
    if (path === '/admin') return { icon: '🔧', title: 'Admin', subtitle: 'System administration and configuration' };
    if (path === '/invite-friends') return { icon: '👥', title: 'Invite Friends', subtitle: 'Share the platform with friends and family' };
    if (path === '/invitations') return { icon: '✉️', title: 'Invitations', subtitle: 'Manage invitation codes and referrals' };
    if (path === '/dynamic-scheduler') return { icon: '🗓️', title: 'Dynamic Scheduler', subtitle: 'Manage scheduled jobs and automation tasks' };
    if (path === '/notification-management') return { icon: '🔔', title: 'Notification Management', subtitle: 'Manage notification queue and templates' };
    if (path === '/activity-logs') return { icon: '📊', title: 'Activity Logs', subtitle: 'Monitor user activities and system events' };
    if (path === '/email-analytics') return { icon: '📧', title: 'Email Analytics', subtitle: 'Track email opens, clicks, and engagement' };
    if (path === '/pause-analytics') return { icon: '⏸️', title: 'Pause Analytics', subtitle: 'Monitor pause feature usage and patterns' };
    if (adminSection === 'announcements') return { icon: '📢', title: 'Announcement Management', subtitle: 'Create and manage site-wide announcements' };
    if (path === '/email-templates') return { icon: '✉️', title: 'Email Templates', subtitle: 'Preview and manage email templates' };
    if (path === '/notification-tester') return { icon: '🧪', title: 'Notification Tester', subtitle: 'Test notification delivery' };
    if (path === '/admin/notification-config') return { icon: '⚙️', title: 'Notification Config', subtitle: 'Configure notification settings' };
    if (path === '/admin/notifications') return { icon: '📬', title: 'Saved Search Notifications', subtitle: 'Manage saved search notification schedules' };
    if (path === '/admin/contact') return { icon: '📩', title: 'Contact Management', subtitle: 'Manage contact form submissions' };
    if (path === '/test-dashboard') return { icon: '🧪', title: 'Test Dashboard', subtitle: 'Testing and development tools' };
    if (path === '/poll-management') return { icon: '📊', title: 'Poll Management', subtitle: 'Create and manage polls' };
    if (path === '/whatsapp-verification') return { icon: '📱', title: 'WhatsApp Verification', subtitle: 'Verify group members against database' };
    if (path === '/testimonials') return { icon: '💬', title: 'Testimonials', subtitle: 'View success stories' };
    if (path === '/contact') return { icon: '📧', title: 'Contact Us', subtitle: 'Get in touch with support' };
    if (path === '/matching-criteria') return { icon: '🎯', title: 'Matching Criteria', subtitle: 'Configure your matching preferences' };
    if (path === '/top-matches') return { icon: '🏆', title: 'Top Matches', subtitle: 'Your best compatibility matches' };
    if (path === '/edit-profile') return { icon: '✏️', title: 'Edit Profile', subtitle: 'Update your profile information' };
    if (path === '/help') return { icon: '❓', title: 'Help Center', subtitle: 'Find answers to common questions' };
    if (path === '/promo-codes') return { icon: '🎫', title: 'Promo Code Manager', subtitle: 'Manage codes, discounts & QR' };
    if (path === '/membership-plans') return { icon: '💳', title: 'Membership Plans', subtitle: 'Configure pricing and plans' };
    if (path === '/lead-generation') return { icon: '📈', title: 'Lead Generation', subtitle: 'Track members & revenue' };
    if (path === '/admin-reports') return { icon: '📊', title: 'Admin Reports', subtitle: 'View system reports and analytics' };
    if (path === '/contribution-management') return { icon: '💝', title: 'Contribution Management', subtitle: 'Track and manage user contributions' };
    if (path === '/admin/recurring-contributions') return { icon: '🔄', title: 'Recurring Contributions', subtitle: 'Manage recurring payment subscriptions' };
    if (path === '/l3v3l-meets') return { icon: '🎥', title: 'Online Events', subtitle: 'Match with participants and connect in 1:1 virtual rooms' };
    if (path === '/pricing') return { icon: '💎', title: 'Membership Pricing', subtitle: 'Choose your membership plan' };
    if (path === '/admin-backups') return { icon: '💾', title: 'Database Backups', subtitle: 'Manage MongoDB backups and restores' };
    if (path === '/admin/inactive-users') return { icon: '🔍', title: 'Inactive Users Report', subtitle: 'Comprehensive report of inactive users' };
    if (path === '/unified-reports') return { icon: '📊', title: 'Reports & Analytics', subtitle: 'View all system reports and analytics in one place' };
    if (path === '/marketing-pricing') return { icon: '🎯', title: 'Marketing & Pricing', subtitle: 'Manage promo codes, pricing, and lead generation' };
    if (path === '/admin-utilities') return { icon: '🔧', title: 'Utilities', subtitle: 'Admin tools and system utilities' };
    if (path === '/admin-hub') return { icon: '🏛️', title: 'Admin Hub', subtitle: 'Contributions, reports, utilities, marketing, blog, and tests in one place' };
    if (path === '/automation') return { icon: '🤖', title: 'Automation', subtitle: 'Scheduler and notification management' };
    if (path === '/member-roles') return { icon: '👥', title: 'Member Roles', subtitle: 'User and role management' };
    if (path === '/support') return { icon: '📧', title: 'Support', subtitle: 'Contact and support management' };
    return null;
  };

  const pageTitle = getPageTitle();

  // Listen for window resize to toggle mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check login status and load user profile
  useEffect(() => {
    const checkLoginStatus = async () => {
      const username = localStorage.getItem('username');
      const token = localStorage.getItem('token');
      
      if (username && token) {
        // SECURITY: Verify token matches username to prevent session contamination
        try {
          // Decode JWT payload (format: header.payload.signature)
          const payloadBase64 = token.split('.')[1];
          const payload = JSON.parse(atob(payloadBase64));
          const tokenUsername = payload.sub; // JWT subject claim contains username
          
          // Check if token username matches stored username
          if (tokenUsername !== username) {
            logger.error('SECURITY: Token/username mismatch detected!');
            logger.error('Stored username:', username);
            logger.error('Token username:', tokenUsername);
            logger.error('Logging out for security...');
            
            // Clear contaminated session
            localStorage.removeItem('username');
            localStorage.removeItem('token');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userStatus');
            setIsLoggedIn(false);
            setCurrentUser(null);
            setUserProfile(null);
            return;
          }
          
          // Token matches username - proceed with login
          logger.info('Token validation passed for user:', username);
          setIsLoggedIn(true);
          setCurrentUser(username);
          
          // Load user role from localStorage
          const role = localStorage.getItem('userRole');
          setUserRole(role);
          
          // Load user profile for display name. Uses the shared getUserProfile
          // so this dedupes with the dashboard's Stage-1 fetch of the same
          // endpoint (the requester param is unused server-side — ownership
          // comes from the JWT).
          try {
            const profileData = await getUserProfile(username);
            setUserProfile(profileData);

            // Update role from profile if available
            if (profileData.role) {
              setUserRole(profileData.role);
            }

            // Load user violations
            loadUserViolations(username);

            // Load membership status
            try {
              const token = localStorage.getItem('token');
              const membershipData = await cachedFetch(
                `contributionStatus:${username}`,
                async () => {
                  const res = await fetch(`${getBackendUrl()}/api/contributions/contribution-status`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  return res.ok ? res.json() : null;
                },
                60000
              );
              if (membershipData) {
                logger.debug('Membership status loaded:', membershipData);
                setMembershipStatus(membershipData.membership);
              } else {
                logger.warn('Membership status API returned non-OK');
              }
            } catch (membershipError) {
              logger.warn('Could not load membership status (non-critical):', membershipError);
            }
          } catch (profileError) {
            // Profile fetch failed - this is OK, don't logout
            // The Profile component will handle displaying the profile
            logger.warn('Could not load user profile in TopBar (non-critical):', profileError?.message || profileError);
            logger.warn('User stays logged in, profile will load from Profile component');
            // IMPORTANT: Do NOT logout here - profile fetch can fail for many reasons
          }
        } catch (error) {
          // This catch is for JWT token decoding errors only
          logger.error('Error decoding or validating JWT token:', error);
          logger.error('This is a critical error - token is invalid or corrupted');
          // Clear session due to token validation failure
          localStorage.removeItem('username');
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userStatus');
          setIsLoggedIn(false);
          setCurrentUser(null);
          setUserProfile(null);
        }
      } else {
        setIsLoggedIn(false);
        setCurrentUser(null);
        setUserProfile(null);
      }
    };

    checkLoginStatus();

    // Listen for storage changes (login/logout from other tabs)
    window.addEventListener('storage', checkLoginStatus);
    
    // Custom event for same-tab login/logout
    window.addEventListener('loginStatusChanged', checkLoginStatus);

    // Listen for membership status changes (e.g., after reset via Activity Summary)
    const handleMembershipChange = () => {
      refreshMembershipStatus();
    };
    window.addEventListener('membershipChanged', handleMembershipChange);

    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener('loginStatusChanged', checkLoginStatus);
      window.removeEventListener('membershipChanged', handleMembershipChange);
    };
  }, []);

  // Online count listener moved to PlatformActivityBar (footer)

  // eslint-disable-next-line no-unused-vars
  const handleLogout = async () => {
    const username = currentUser;
    
    // Disconnect WebSocket
    logger.info('Disconnecting WebSocket');
    socketService.disconnect();
    
    // Mark user as offline (non-blocking beacon)
    if (username) {
      navigator.sendBeacon(
        `${getApiUrl()}/online-status/${username}/offline`,
        ''
      );
    }
    
    localStorage.removeItem('username');
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('appTheme'); // Clear theme cache
    sessionStorage.removeItem('urgencyModalShown'); // Reset urgency modal for next login
    setIsLoggedIn(false);
    setCurrentUser(null);
    
    // Dispatch custom event for sidebar to update
    window.dispatchEvent(new Event('loginStatusChanged'));
    
    navigate('/');
  };

  const handleLogoutAndForgetDevice = async () => {
    const username = currentUser;

    // Disconnect WebSocket
    logger.info('Disconnecting WebSocket and forgetting device');
    socketService.disconnect();

    // Mark user as offline (non-blocking beacon)
    if (username) {
      navigator.sendBeacon(
        `${getApiUrl()}/online-status/${username}/offline`,
        ''
      );
    }

    // Revoke this specific trusted device on the backend before clearing storage.
    try {
      const deviceId = getOrCreateTrustedDeviceId();
      await revokeTrustedDevice(deviceId, getTrustedDeviceAppId());
    } catch (revokeError) {
      logger.warn('Failed to revoke trusted device on backend:', revokeError);
    }

    // Clear all local trusted-device and biometric credentials.
    clearAllTrustedDeviceTokens();
    try {
      await clearCredential();
    } catch (_) {
      // Native credential may not exist; ignore.
    }

    localStorage.removeItem('username');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('homePage');
    localStorage.removeItem('appTheme');
    sessionStorage.removeItem('urgencyModalShown');
    setIsLoggedIn(false);
    setCurrentUser(null);

    window.dispatchEvent(new Event('loginStatusChanged'));

    navigate('/');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleProfile = () => {
    if (currentUser) {
      navigate(`/profile/${currentUser}`);
    }
  };

  // Load user violations
  const loadUserViolations = async (username) => {
    try {
      const response = await api.get(`/violations/${username}`);
      if (response.data && response.data.violationCount > 0) {
        setViolations(response.data);
      } else {
        setViolations(null); // Clear violations if count is 0
      }
    } catch (error) {
      logger.error('Error loading user violations:', error);
    }
  };

  // Listen for violation updates
  useEffect(() => {
    if (!currentUser) return;

    const handleViolationUpdate = () => {
      loadUserViolations(currentUser);
    };

    window.addEventListener('violationUpdate', handleViolationUpdate);

    return () => {
      window.removeEventListener('violationUpdate', handleViolationUpdate);
    };
  }, [currentUser]);

  // Poll for admin pending counts (admin only) - unified endpoint
  useEffect(() => {
    if (userRole !== 'admin') {
      setAdminPendingCounts(null);
      return;
    }

    const fetchAdminPendingCounts = async () => {
      try {
        const adminApi = createApiInstance();
        const response = await adminApi.get('/api/admin/pending-counts');
        setAdminPendingCounts(response.data);
      } catch (error) {
        logger.error('Error fetching admin pending counts:', error);
      }
    };

    fetchAdminPendingCounts();
    const interval = setInterval(() => {
      if (!document.hidden) fetchAdminPendingCounts();
    }, 120000); // Reduced from 60s to 120s
    return () => clearInterval(interval);
  }, [userRole]);

  // Close admin actions dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (adminActionsRef.current && !adminActionsRef.current.contains(e.target)) {
        setShowAdminActions(false);
      }
    };
    if (showAdminActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdminActions]);

  // Close user menu dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close search menu dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchMenuRef.current && !searchMenuRef.current.contains(e.target)) {
        setShowSearchMenu(false);
      }
    };
    if (showSearchMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchMenu]);

  useEffect(() => {
    const handleClickOutsideMessenger = (e) => {
      if (messengerMenuRef.current && !messengerMenuRef.current.contains(e.target)) {
        setShowMessengerMenu(false);
      }
    };

    if (showMessengerMenu) {
      document.addEventListener('mousedown', handleClickOutsideMessenger);
    }

    return () => document.removeEventListener('mousedown', handleClickOutsideMessenger);
  }, [showMessengerMenu]);

  // Load saved searches for the search menu when it opens
  useEffect(() => {
    if (!showSearchMenu || !currentUser) return;

    const now = Date.now();
    if (savedSearchesForMenuLoadedAt && (now - savedSearchesForMenuLoadedAt) < 30000) {
      return;
    }

    let cancelled = false;
    const loadSavedSearchesForMenu = async () => {
      setSavedSearchesForMenuLoading(true);
      try {
        // Shares the dashboardv2 fetchSavedSearches cache key — opening the
        // menu right after a dashboard load reuses that response.
        const savedSearches = await cachedFetch(
          `savedSearches:${currentUser}`,
          async () => (await api.get(`/${currentUser}/saved-searches`)).data.savedSearches || [],
          30000
        );
        if (cancelled) return;
        setSavedSearchesForMenu(savedSearches);
        setSavedSearchesForMenuLoadedAt(Date.now());
      } catch (err) {
        logger.error('Error loading saved searches for TopBar menu:', err);
        if (cancelled) return;
        setSavedSearchesForMenu([]);
      } finally {
        if (cancelled) return;
        setSavedSearchesForMenuLoading(false);
      }
    };

    loadSavedSearchesForMenu();
    return () => {
      cancelled = true;
    };
  }, [currentUser, savedSearchesForMenuLoadedAt, showSearchMenu]);

  const handleSearchProfilesClick = () => {
    setShowSearchMenu(false);
    if (location.pathname === '/search') {
      window.dispatchEvent(new Event('openSearchModal'));
      return;
    }

    sessionStorage.setItem('pendingSearchAction', JSON.stringify({ type: 'openFilters' }));
    navigate('/search');
  };

  const handleNearMeClick = (radiusMiles = 100) => {
    setShowSearchMenu(false);
    if (location.pathname === '/search') {
      window.dispatchEvent(new CustomEvent('runNewMeSearchFromTopbar', {
        detail: { radiusMiles }
      }));
      return;
    }

    sessionStorage.setItem('pendingSearchAction', JSON.stringify({ type: 'newMeSearch', radiusMiles }));
    navigate('/search');
  };

  const handleNearMeRadiusChange = (event) => {
    setNearMeRadiusMiles(clampNearMeRadius(event.target.value));
  };

  const adjustNearMeRadius = (delta) => {
    setNearMeRadiusMiles((prev) => clampNearMeRadius(prev + delta));
  };

  const handleTopbarSavedSearchClick = (savedSearch) => {
    if (!savedSearch) return;
    setShowSearchMenu(false);
    if (location.pathname === '/search') {
      window.dispatchEvent(new CustomEvent('loadSavedSearchFromTopbar', { detail: savedSearch }));
      return;
    }

    sessionStorage.setItem('pendingSearchAction', JSON.stringify({
      type: 'loadSavedSearch',
      savedSearch
    }));
    navigate('/search');
  };

  // Poll for unread admin responses (all logged-in users)
  useEffect(() => {
    if (!currentUser) {
      setUnreadResponseCount(0);
      return;
    }

    const fetchUnreadResponseCount = async () => {
      try {
        const response = await api.get(`/contact/user/${currentUser}/unread-count`);
        setUnreadResponseCount(response.data.count || 0);
      } catch (error) {
        logger.error('Error fetching unread response count:', error);
      }
    };

    // Fetch immediately
    fetchUnreadResponseCount();

    // Poll every 90 seconds (reduced from 60s to prevent too frequent updates)
    const interval = setInterval(() => {
      if (!document.hidden) fetchUnreadResponseCount();
    }, 90000);

    return () => clearInterval(interval);
  }, [currentUser]);

  // Poll for critical messages count
  useEffect(() => {
    if (!currentUser) {
      setUrgencyCounts({ pending: 0, medium: 0, high: 0, critical: 0 });
      return;
    }

    const fetchCriticalMessagesCount = async () => {
      try {
        // Shared cache: RecentConversations polls the same endpoint, and the
        // inflight dedup collapses the simultaneous mount-time calls to one.
        const data = await cachedFetch(
          `unattendedConversations:${currentUser}`,
          async () => (await api.get('/messages/unattended')).data,
          25000
        );
        setUrgencyCounts({
          pending: data.pendingCount || 0,
          medium: data.mediumCount || 0,
          high: data.highCount || 0,
          critical: data.criticalCount || 0
        });
        setUnattendedConversations(data.conversations || []);
      } catch (error) {
        logger.error('Error fetching critical messages count:', error);
      }
    };

    // Fetch immediately
    fetchCriticalMessagesCount();

    // Poll every 30 seconds for critical messages (more frequent since it's urgent)
    const interval = setInterval(() => {
      if (!document.hidden) fetchCriticalMessagesCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const launchMessengerApp = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');
      const baseUrl = getMessengerUrl();

      try {
        logActivity(ActivityType.MESSENGER_OPENED, {
          source: 'topbar',
          target_url: baseUrl,
          has_sso_token: !!token,
        });
      } catch (_) {
        // logging failures should not block launcher
      }

      const params = new URLSearchParams();
      if (token) params.set('token', token);
      if (username) params.set('u', username);
      const url = params.toString() ? `${baseUrl}/?${params.toString()}` : baseUrl;

      const width = 420;
      const height = 820;
      const left = Math.max(0, (window.screen.availWidth - width) - 40);
      const top = Math.max(0, (window.screen.availHeight - height) / 2);
      const features = [
        `width=${width}`,
        `height=${height}`,
        `left=${left}`,
        `top=${top}`,
        'menubar=no',
        'toolbar=no',
        'location=no',
        'status=no',
        'resizable=yes',
        'scrollbars=yes',
        'noopener=no',
      ].join(',');

      const popup = window.open(url, 'l3v3lMessenger', features);
      if (popup) {
        popup.focus();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setShowMessengerMenu(false);
    }
  }, [ActivityType, logActivity]);

  // Auto-show urgency modal once per login session (or when urgency escalates)
  const prevUrgencyRef = useRef(null);
  useEffect(() => {
    if (!urgencyLevel) {
      prevUrgencyRef.current = null;
      return;
    }
    const shownLevel = sessionStorage.getItem('urgencyModalShown');
    const urgencyRank = { pending: 1, medium: 2, high: 3, critical: 4 };
    const currentRank = urgencyRank[urgencyLevel] || 0;
    const shownRank = urgencyRank[shownLevel] || 0;

    // Show on first detection after login OR when urgency escalates beyond what was already shown
    if (!shownLevel || currentRank > shownRank) {
      setShowUnattendedAlert(true);
      sessionStorage.setItem('urgencyModalShown', urgencyLevel);
    }
    prevUrgencyRef.current = urgencyLevel;
  }, [urgencyLevel]);

  if (!isLoggedIn) {
    return (
      <div className={`top-bar ${isOpen ? (isPinned ? 'sidebar-pinned' : 'sidebar-open') : ''}`}>
        <div className="top-bar-content">
          <div className="top-bar-left">
            <button className="sidebar-toggle-btn" onClick={onSidebarToggle} title="Toggle Sidebar">
              ☰
            </button>
            <div className="" onClick={() => navigate('/dashboardv2')}>
              <img
                src="/new_logo1.png"
                alt="L3V3L Matches"
                className="app-logo-image"
              />
            </div>
          </div>
          <div className="top-bar-right">
            <button className="btn-login" onClick={handleLogin} title="Login">
              🔑
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Header - shown only on mobile, positioned at very top of page */}
      {isMobile && (
        <div className="mobile-messenger-header">
          <span className="mobile-header-title">
            <img
              src="/new_logo1.png"
              alt=""
              className="mobile-header-logo"
              aria-hidden="true"
            />
            <span>L3V3L Matches</span>
          </span>
          <span className="mobile-header-online">{onlineCount} online</span>
        </div>
      )}
      <div className={`top-bar ${isOpen ? (isPinned ? 'sidebar-pinned' : 'sidebar-open') : ''}`}>
      {/* Violation Warning Banner */}
      {violations && violations.violationCount > 0 && (
        <div className={`violation-banner violation-${violations.warningLevel}`}>
          <span className="violation-icon">
            {violations.warningLevel === 'banned' ? '🚫' : violations.warningLevel === 'suspended' ? '⏸️' : '⚠️'}
          </span>
          <span className="violation-message">{violations.warningMessage}</span>
          <span className="violation-count">{violations.violationCount}/3</span>
        </div>
      )}
      <div className="top-bar-content">
        <div className="top-bar-left">
          <button className="sidebar-toggle-btn" onClick={onSidebarToggle} title="Toggle Sidebar">
            ☰
          </button>
          <div className="" onClick={() => navigate('/dashboardv2')}>
            <img
              src="/new_logo1.png"
              alt="L3V3L Matches"
              className="app-logo-image"
            />
          </div>
          {/* Branding text - merged from BrandBanner */}
          {brandConfig && (
            <div className="topbar-branding">
              <span className="topbar-brand-name">{brandConfig.branding.appName}</span>
              {!isMobile && brandConfig.branding.tagline && (
                <span className="topbar-brand-tagline">{brandConfig.branding.tagline}</span>
              )}
            </div>

          )}
          {/* Event Countdown - Shows for users who RSVPed "Yes" to upcoming events */}
          <EventCountdown />
        </div>
        <div className="top-bar-right">
          <div className="search-menu-container" ref={searchMenuRef}>
            <button
              className={`btn-refer-friend ${showSearchMenu ? 'is-open' : ''}`}
              onClick={() => setShowSearchMenu(prev => !prev)}
              title="Search"
              aria-haspopup="menu"
              aria-expanded={showSearchMenu}
            >
              <span className="refer-icon">🔍</span>
              <span className="refer-text">Search</span>
            </button>
            {showSearchMenu && (
              <div className="search-menu-dropdown" role="menu" aria-label="Search menu">
                <div className="search-menu-header">Search</div>
                <button type="button" className="search-menu-item" onClick={handleSearchProfilesClick}>
                  <span className="search-menu-item-icon">🔍</span>
                  <span className="search-menu-item-label">Search Profiles</span>
                </button>
                <div
                  className="search-menu-near-me-inline"
                  role="button"
                  tabIndex={0}
                  aria-label="Run Near Me search"
                  onClick={() => handleNearMeClick(nearMeRadiusMiles)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNearMeClick(nearMeRadiusMiles);
                    }
                  }}
                >
                  <span className="search-menu-item-icon" aria-hidden="true">📍</span>
                  <button
                    type="button"
                    className="search-menu-near-me-trigger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNearMeClick(nearMeRadiusMiles);
                    }}
                  >
                    Near Me
                  </button>
                  <button
                    type="button"
                    className="search-menu-radius-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      adjustNearMeRadius(-10);
                    }}
                    aria-label="Decrease Near Me radius"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="search-menu-radius-input"
                    min="10"
                    max="500"
                    step="10"
                    value={nearMeRadiusMiles}
                    onChange={handleNearMeRadiusChange}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Near Me radius miles"
                  />
                  <span className="search-menu-radius-unit">mi</span>
                  <button
                    type="button"
                    className="search-menu-radius-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      adjustNearMeRadius(10);
                    }}
                    aria-label="Increase Near Me radius"
                  >
                    +
                  </button>
                  {showNearMeNewBadge && (
                    <span className="search-menu-new-pill" aria-label="New feature">NEW</span>
                  )}
                </div>
                <div className="search-menu-divider" />
                <div className="search-menu-header">Saved Searches</div>
                <div className="search-menu-saved-list" role="group" aria-label="Saved searches">
                  {savedSearchesForMenuLoading ? (
                    <div className="search-menu-state">Loading…</div>
                  ) : savedSearchesForMenu.length === 0 ? (
                    <div className="search-menu-state">No saved searches yet</div>
                  ) : (
                    savedSearchesForMenu.map((savedSearch) => (
                      <button
                        key={savedSearch._id || savedSearch.id || savedSearch.name}
                        type="button"
                        className="search-menu-item search-menu-saved-item"
                        onClick={() => handleTopbarSavedSearchClick(savedSearch)}
                      >
                        <span className="search-menu-item-icon" role="img" aria-label="Saved search">📌</span>
                        <span className="search-menu-item-label">{savedSearch.name || 'Saved search'}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Admin Action Center - Unified pending items indicator */}
          {userRole === 'admin' && adminPendingCounts && (
            <div className="admin-actions-container" ref={adminActionsRef}>
              <button 
                className={`btn-admin-actions ${adminPendingCounts.total > 0 ? 'has-pending' : ''}`}
                onClick={() => setShowAdminActions(!showAdminActions)}
                title={`Admin Actions${adminPendingCounts.total > 0 ? ` (${adminPendingCounts.total} pending)` : ''}`}
              >
                🔔
                {adminPendingCounts.total > 0 && (
                  <span className="admin-actions-badge">{adminPendingCounts.total > 99 ? '99+' : adminPendingCounts.total}</span>
                )}
              </button>
              {showAdminActions && (
                <div className="admin-actions-dropdown">
                  <div className="admin-actions-header">Admin Actions Needed</div>
                  <button 
                    className="admin-action-item"
                    onClick={() => { navigate('/admin'); setShowAdminActions(false); }}
                  >
                    <span className="action-icon">👤</span>
                    <span className="action-label">Users pending approval</span>
                    <span className={`action-count ${adminPendingCounts.pendingApprovals > 0 ? 'has-items' : ''}`}>
                      {adminPendingCounts.pendingApprovals}
                    </span>
                  </button>
                  <button 
                    className="admin-action-item"
                    onClick={() => { navigate('/admin/contact'); setShowAdminActions(false); }}
                  >
                    <span className="action-icon">🎫</span>
                    <span className="action-label">Open support tickets</span>
                    <span className={`action-count ${adminPendingCounts.openTickets > 0 ? 'has-items' : ''}`}>
                      {adminPendingCounts.openTickets}
                    </span>
                  </button>
                  <button 
                    className="admin-action-item"
                    onClick={() => { navigate('/admin?tab=interests'); setShowAdminActions(false); }}
                  >
                    <span className="action-icon">📋</span>
                    <span className="action-label">Pending registrations</span>
                    <span className={`action-count ${adminPendingCounts.pendingRegistrations > 0 ? 'has-items' : ''}`}>
                      {adminPendingCounts.pendingRegistrations ?? 0}
                    </span>
                  </button>
                  <button 
                    className="admin-action-item"
                    onClick={() => { navigate('/testimonials'); setShowAdminActions(false); }}
                  >
                    <span className="action-icon">⭐</span>
                    <span className="action-label">Pending testimonials</span>
                    <span className={`action-count ${adminPendingCounts.pendingTestimonials > 0 ? 'has-items' : ''}`}>
                      {adminPendingCounts.pendingTestimonials}
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Support Response Alert - All Users (when they have unread admin responses) */}
          {unreadResponseCount > 0 && (
            <button 
              className="btn-support-response" 
              onClick={() => navigate('/contact')}
              title={`You have ${unreadResponseCount} unread support response${unreadResponseCount > 1 ? 's' : ''}`}
            >
              📩
              <span className="support-response-badge">{unreadResponseCount > 99 ? '99+' : unreadResponseCount}</span>
            </button>
          )}
          
          {/* Messenger App Launcher - opens standalone messenger in mobile-sized popup window with SSO */}
          <div className="messenger-menu-container" ref={messengerMenuRef}>
            <button
              className={`btn-messenger-app ${showMessengerMenu ? 'is-open' : ''}`}
              onClick={() => setShowMessengerMenu(prev => !prev)}
              title="Messenger options"
              aria-haspopup="menu"
              aria-expanded={showMessengerMenu}
              type="button"
            >
              <span className="messenger-launcher-icon" aria-hidden="true">💬</span>
            </button>
            {showMessengerMenu && (
              <div className="messenger-menu-dropdown" role="menu" aria-label="Messenger actions">
                <button
                  type="button"
                  className="messenger-menu-item"
                  onClick={launchMessengerApp}
                  role="menuitem"
                >
                  <span className="messenger-menu-item-icon" aria-hidden="true">🚀</span>
                  <span className="messenger-menu-item-label">Open Messenger App</span>
                </button>
                <a
                  className="messenger-menu-item"
                  href="https://play.google.com/store/apps/details?id=com.l3v3lmessenger"
                  target="_blank"
                  rel="noopener noreferrer"
                  role="menuitem"
                  onClick={() => setShowMessengerMenu(false)}
                >
                  <span className="messenger-menu-item-icon" aria-hidden="true">📱</span>
                  <span className="messenger-menu-item-label">Download Android App</span>
                </a>
              </div>
            )}
          </div>

          {/*
            Topbar messages dropdown intentionally disabled.
            Dashboard V2 Recent Conversations now owns the primary message-entry UX.
            Keep this block commented for quick restore if needed.
          */}
          {/*
          <div className="messages-icon-container">
            <button className={`btn-messages ${urgencyLevel || ''}`} type="button" title="Messages">
              💬
            </button>
          </div>
          */}
          
          {/* Online member count moved to PlatformActivityBar (footer) */}

          {/* User Avatar Dropdown */}
          {isLoggedIn && (
            <div className="user-menu-container" ref={userMenuRef}>
              <button
                className="btn-user-avatar"
                onClick={() => setShowUserMenu(!showUserMenu)}
                title={(getShortName(userProfile) || currentUser || 'User')}
              >
                {getProfilePicUrl(userProfile) ? (
                  <img src={getProfilePicUrl(userProfile)} alt="" className="user-avatar-img" />
                ) : (
                  <span className="user-avatar-initials">
                    {(getShortName(userProfile) || currentUser || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </button>
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div
                    className="user-menu-header"
                    onClick={() => { navigate(`/profile/${currentUser}`); setShowUserMenu(false); }}
                  >
                    <div className="user-menu-avatar">
                      {getProfilePicUrl(userProfile) ? (
                        <img src={getProfilePicUrl(userProfile)} alt="" />
                      ) : (
                        <span>{(getShortName(userProfile) || currentUser || 'U').charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div className="user-menu-info">
                      <span className="user-menu-name">{getShortName(userProfile) || currentUser || 'User'}</span>
                      <span className="user-menu-subtitle">Profile data</span>
                      {membershipStatus && (
                        <span className="user-menu-membership-status">
                          {membershipStatus.hasAccess ? (
                            <span className="membership-active">
                              {getMembershipMicroLabel(membershipStatus)}
                            </span>
                          ) : (
                            <span className="membership-inactive">No membership</span>
                          )}
                          {!isNativePlatform() && (
                          <span className="membership-explanation" title="Single payment ≥ $60 unlocks tier benefits. Amounts < $60 give $1/day access.">
                            💡 Single payment ≥ $60 for tier benefits
                          </span>
                          )}
                        </span>
                      )}
                      {!membershipStatus && (
                        <span className="user-menu-membership-status">
                          <span className="membership-inactive">Loading...</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="user-menu-divider" />
                  {/* Hidden in native app (Play policy) and for activated
                      members — paid members get no money asks. Non-activated
                      members see the membership activation entry point. */}
                  {!isNativePlatform() && !membershipStatus?.hasAccess && userRole !== 'admin' && userRole !== 'moderator' && (
                  <button
                    className="user-menu-item support-l3v3l"
                    onClick={() => { openPopup(); setShowUserMenu(false); }}
                  >
                    <span className="user-menu-icon">🔓</span>
                    <div className="user-menu-item-content">
                      <span className="user-menu-item-label">Activate membership</span>
                      <span className="user-menu-item-sublabel">Unlock member features</span>
                    </div>
                  </button>
                  )}
                  <button
                    className="user-menu-item"
                    onClick={() => { navigate('/preferences'); setShowUserMenu(false); }}
                  >
                    <span className="user-menu-icon">⚙️</span>
                    <div className="user-menu-item-content">
                      <span className="user-menu-item-label">Settings</span>
                      <span className="user-menu-item-sublabel">Preferences, Theme & Notifications</span>
                    </div>
                  </button>
                  <button
                    className="user-menu-item"
                    onClick={() => { navigate('/support'); setShowUserMenu(false); }}
                  >
                    <span className="user-menu-icon">📧</span>
                    <div className="user-menu-item-content">
                      <span className="user-menu-item-label">Support</span>
                      <span className="user-menu-item-sublabel">Get help & contact support</span>
                    </div>
                  </button>
                  <div className="user-menu-divider" />
                  <button
                    className="user-menu-item logout"
                    onClick={() => { handleLogout(); setShowUserMenu(false); }}
                  >
                    <span className="user-menu-icon">🚪</span>
                    <span className="user-menu-item-label">Logout</span>
                  </button>
                  <button
                    className="user-menu-item logout"
                    onClick={() => { handleLogoutAndForgetDevice(); setShowUserMenu(false); }}
                  >
                    <span className="user-menu-icon">🧹</span>
                    <span className="user-menu-item-label">Logout-forget</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Topbar message modal disabled along with topbar messages dropdown. */}
      </div>

      {/* InfoTicker - shown on desktop only, completely hidden on mobile */}
      {!isMobile && <InfoTicker />}

      {/* Page Title Section - Horizontal bar below ticker */}
      {pageTitle && (
        <div className="page-title-section">
          <span className="page-title-icon">{pageTitle.icon}</span>
          <div className="page-title-content">
            <span className="page-title-text">{pageTitle.title}</span>
            {pageTitle.subtitle && (
              <span className="page-title-subtitle">{pageTitle.subtitle}</span>
            )}
          </div>
        </div>
      )}

      {/* Urgency Popup Modal — rendered outside top-bar so it overlays the whole page */}
      {showUnattendedAlert && urgencyLevel && unattendedConversations.length > 0 && (
        <div className="urgency-modal-backdrop" onClick={() => setShowUnattendedAlert(false)}>
          <div className="urgency-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`urgency-modal-header urgency-${urgencyLevel}`}>
              <span className="urgency-modal-title">
                {urgencyLevel === 'critical' && `🔴 ${urgencyCounts.critical} Critical Message${urgencyCounts.critical > 1 ? 's' : ''}`}
                {urgencyLevel === 'high' && `🟠 ${urgencyCounts.high} Message${urgencyCounts.high > 1 ? 's' : ''} Waiting 6-9 Days`}
                {urgencyLevel === 'medium' && `🟡 ${urgencyCounts.medium} Message${urgencyCounts.medium > 1 ? 's' : ''} Waiting 3-5 Days`}
                {urgencyLevel === 'pending' && `💬 ${urgencyCounts.pending} Message${urgencyCounts.pending > 1 ? 's' : ''} Waiting 1-2 Days`}
              </span>
              <button className="urgency-modal-close" onClick={() => setShowUnattendedAlert(false)}>✕</button>
            </div>
            <div className="urgency-modal-body">
              <p className="urgency-modal-message">
                {urgencyLevel === 'critical' && 'These messages have been waiting over 10 days. Please respond or politely decline.'}
                {urgencyLevel === 'high' && 'These messages are about to become critical. Please reply soon to keep the conversation going.'}
                {urgencyLevel === 'medium' && 'Someone took the time to reach out to you. A timely response would be appreciated.'}
                {urgencyLevel === 'pending' && 'You have new messages waiting for your reply.'}
              </p>
              <div className="urgency-modal-list">
                {unattendedConversations.map((item, idx) => (
                  <div key={idx} className="urgency-modal-item">
                    <div className={`urgency-modal-dot ${item.urgency}`} />
                    <div className="urgency-modal-item-info">
                      <span className="urgency-modal-name">{item.sender?.firstName} {item.sender?.lastName}</span>
                      <span className="urgency-modal-preview">{item.lastMessage?.text?.substring(0, 60)}{item.lastMessage?.text?.length > 60 ? '…' : ''}</span>
                    </div>
                    <span className={`urgency-modal-days ${item.urgency}`}>{item.lastMessage?.waitingDays}d</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="urgency-modal-footer">
              <button className="urgency-modal-cancel" onClick={() => setShowUnattendedAlert(false)}>Cancel</button>
              <button className="urgency-modal-goto" onClick={() => { setShowUnattendedAlert(false); navigate('/messages'); }}>Go to Messages →</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default TopBar;
